if (!customElements.get('sticky-atc-panel')) {
  class StickyAtcPanel extends HTMLElement {
    constructor() {
      super();

      this.productSection = this.closest('.cc-main-product');
      this.productInfo    = this.productSection.querySelector('.product-info');
      this.productForm    = this.productSection.querySelector('product-form');

      if ('IntersectionObserver' in window && 'MutationObserver' in window) {
        this.bindEvents();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', StickyAtcPanel.handleScroll);
    }

    bindEvents() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          /* ▼-- existing theme logic – don’t change --▼ */
          if (entry.target === this.productForm && !theme.mediaMatches.md) {
            this.classList.toggle(
              'sticky-atc-panel--out',
              entry.boundingClientRect.bottom > 0
            );
          } else if (entry.target === this.productInfo && theme.mediaMatches.md) {
            this.classList.toggle('sticky-atc-panel--out', entry.isIntersecting);
          }
          /* ▲-- existing theme logic – don’t change --▲ */

          /* ───────────  NEW CODE  ─────────── */
          const header       = document.getElementById('site-header');
          const barIsVisible = !this.classList.contains('sticky-atc-panel--out');
          if (header) header.classList.toggle('hide', barIsVisible);
          /* ─────────────────────────────────── */
        });
      });

      if (this.productForm) observer.observe(this.productForm);
      if (this.productInfo) observer.observe(this.productInfo);

      window.addEventListener('scroll', StickyAtcPanel.handleScroll);
    }

    static handleScroll() {
      document.body.classList.toggle(
        'scrolled-to-bottom',
        window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
      );
    }
  }

  customElements.define('sticky-atc-panel', StickyAtcPanel);
}

// Qualified CA purchase bar. The original product-form remains the sole cart writer.
if (!customElements.get('ca-sticky-purchase')) {
  class CaStickyPurchase extends HTMLElement {
    connectedCallback() {
      this.panel = this.closest('sticky-atc-panel');
      this.section = this.closest('.cc-main-product');
      this.info = this.section?.querySelector('.product-info');
      if (!this.panel || !this.info) return;
      this.action = this.querySelector('[data-ca-sticky-action]');
      this.selection = this.querySelector('[data-ca-sticky-selection]');
      this.price = this.querySelector('[data-ca-sticky-price]');
      this.status = this.querySelector('[data-ca-sticky-status]');
      this.heading = this.querySelector('.sticky-atc-panel__heading');
      this.optionsLink = this.querySelector('[data-ca-sticky-options]');
      this.verifiedId = this.dataset.variantId || '';
      this.available = this.dataset.available === 'true';
      this.allowDirectContext = !this.info.querySelector('.product-options [data-product-url]:not([data-product-url=""])');
      try { this.verifiedOptions = JSON.parse(this.dataset.options || '[]'); } catch (_) { this.verifiedOptions = []; }
      this.abort = new AbortController();
      const opts = { signal: this.abort.signal };
      this.section.addEventListener('change', (e) => this.onInput(e), { ...opts, capture: true });
      this.section.addEventListener('input', (e) => this.onInput(e), { ...opts, capture: true });
      this.section.addEventListener('on:variant:change', (e) => this.onVariant(e), opts);
      ['on:cart:add', 'on:line-item:change', 'on:cart:error'].forEach((name) => {
        this.section.addEventListener(name, (e) => {
          if (e.target !== this.mainComponent()) return;
          this.outcome = name === 'on:cart:error' ? 'error' : 'success';
          this.say(this.outcome === 'success' ? 'Added to cart.' : (e.detail?.error || 'Could not add. Review the purchase options and try again.'));
          this.queue();
        }, opts);
      });
      this.action.addEventListener('click', () => this.activate(), opts);
      this.selection.addEventListener('click', (e) => {
        if (e.target.closest('[data-ca-sticky-quantity]')) this.focusQuantity();
      }, opts);
      this.optionsLink.addEventListener('click', (e) => {
        e.preventDefault();
        this.focusOptions();
      }, opts);
      this.observer = new MutationObserver(() => this.queue());
      this.observer.observe(this.info, { subtree: true, childList: true, characterData: true, attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class', 'value', 'hidden'] });
      this.observer.observe(this.panel, { attributes: true, attributeFilter: ['class'] });
      this.resizeObserver = new ResizeObserver(() => this.updateHelp());
      this.resizeObserver.observe(this.panel);
      window.addEventListener('resize', () => this.queue(), opts);
      window.addEventListener('scroll', () => this.queue(), { ...opts, passive: true });
      this.discovery = new MutationObserver(() => this.attachHelp());
      this.discovery.observe(document.body, { childList: true });
      this.attachHelp();
      customElements.whenDefined('clever-search-toggler').then(() => {
        if (this.isConnected) requestAnimationFrame(() => this.attachHelp());
      });
      this.sync();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this.discovery?.disconnect();
      this.chatObserver?.disconnect();
      this.togglerObserver?.disconnect();
      this.helpObserver?.disconnect();
      clearTimeout(this.variantTimer);
      cancelAnimationFrame(this.frame);
      this.restoreHelp();
    }

    mainForm() { return document.getElementById(this.dataset.formId); }
    mainComponent() { return this.mainForm()?.closest('product-form'); }

    optionState() {
      const groups = [...this.info.querySelectorAll('.product-options .option-selector:not(.option-selector--custom)')];
      const values = groups.map((group) => {
        const checked = group.querySelector('input[type="radio"]:checked');
        if (checked) return checked.value;
        const selected = group.querySelector('.custom-select__option[aria-selected="true"]');
        if (selected && selected.dataset.value) return selected.dataset.value;
        const select = group.querySelector('select');
        return select?.value || null;
      });
      const hasAlternatives = groups.some((group) => {
        const controls = [...group.querySelectorAll('input[type="radio"], select option, .custom-select__option')];
        const choices = controls.map((el) => el.dataset.value ?? el.value).filter((v) => v != null && v !== '');
        // Keep unavailable alternatives visible too; stock does not remove a real choice.
        return new Set(choices).size > 1 || !controls.length;
      });
      return { values, hasAlternatives, complete: values.every((v) => v !== null && v !== '') };
    }

    onInput(e) {
      if (!this.info.contains(e.target)) return;
      if (this.outcome) { this.outcome = null; this.say(''); }
      if (e.type === 'change' && e.target.closest('.product-options')) {
        this.variantPending = true;
        this.variantFailed = false;
        this.outcome = null;
        clearTimeout(this.variantTimer);
        this.variantTimer = setTimeout(() => {
          if (!this.variantPending || !this.isConnected) return;
          this.variantFailed = true;
          this.say('The selection is still updating. Review the purchase options.');
          this.queue();
        }, 8000);
      }
      this.queue();
    }

    onVariant(e) {
      if (e.detail?.form !== this.mainForm()) return;
      const variant = e.detail.variant;
      const selected = this.optionState();
      const options = variant?.options || [variant?.option1, variant?.option2, variant?.option3].filter((v) => v != null);
      // A late event must never authorize an old variant against newer choices.
      if (variant && selected.values.length && JSON.stringify(selected.values) !== JSON.stringify(options)) return;
      if (!variant && JSON.stringify(selected.values) !== JSON.stringify(e.detail.selectedOptions || [])) return;
      this.verifiedId = variant ? String(variant.id) : '';
      this.available = variant?.available === true;
      this.verifiedOptions = options || [];
      this.variantPending = false;
      this.variantFailed = false;
      clearTimeout(this.variantTimer);
      this.outcome = null;
      this.say('');
      this.queue();
    }

    state() {
      const form = this.mainForm();
      const component = form?.closest('product-form');
      const native = form?.querySelector('[name="add"]');
      const id = form?.querySelector('[name="id"]')?.value || '';
      const quantityInput = form?.querySelector('[name="quantity"]');
      const quantity = quantityInput?.value || '';
      const choices = this.optionState();
      const optionsMatch = !choices.values.length || JSON.stringify(choices.values) === JSON.stringify(this.verifiedOptions);
      const matched = !!id && id === this.verifiedId && optionsMatch && !this.variantPending;
      const invalid = form ? [...form.elements].find((el) => el.willValidate && !el.validity.valid) : null;
      const validQuantity = /^\d+$/.test(quantity) && Number(quantity) > 0 && Number.isSafeInteger(Number(quantity));
      const nativeBusy = !!native && (native.getAttribute('aria-disabled') === 'true' || native.classList.contains('is-loading'));
      const backorder = !!this.info.querySelector('.product-backorder')?.textContent.trim();
      const context = this.section.querySelector('[data-ca-sticky-context]');
      const qualified = context?.dataset.qualified === 'true' && this.allowDirectContext
        && this.info.querySelector('variant-picker')?.dataset.availability !== 'prune'
        && !this.info.querySelector('.product-options [data-product-url]:not([data-product-url=""]), [name="selling_plan"], [name^="properties["], .shopify-payment-button');
      const nativeBound = component?.form === form && typeof component.handleSubmit === 'function'
        && window.theme?.settings?.afterAtc && window.theme.settings.afterAtc !== 'no-js';
      const ready = !!native && choices.complete && matched && this.available && !native.disabled
        && !nativeBusy && !invalid && validQuantity && nativeBound && !backorder && qualified;
      return { form, component, native, id, quantityInput, quantity, choices, matched, invalid, validQuantity, nativeBusy, ready };
    }

    queue() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = null; if (this.isConnected) this.sync(); });
    }

    say(message) {
      if (this.status.textContent !== String(message)) this.status.textContent = String(message);
      this.status.classList.add('text-sm');
      this.status.classList.toggle('visually-hidden', !['success', 'error'].includes(this.outcome));
    }
    setText(el, text) { if (el.textContent !== text) el.textContent = text; }

    sync() {
      this.updatePosition();
      const s = this.state();
      if (!s.form || !s.native) {
        this.addReady = false;
        this.action.disabled = false;
        this.setText(this.action, 'Go to purchase options');
        this.updateHelp();
        return;
      }
      if (this.pending && !s.nativeBusy) this.pending = false;
      const title = this.info.querySelector('.product-info__title .product-title')?.textContent.trim();
      if (title) this.setText(this.heading, title);
      const updating = this.variantPending && !this.variantFailed;
      this.syncSelection(s, updating);
      this.optionsLink.hidden = !s.choices.hasAlternatives;
      this.setText(this.optionsLink, 'Change options');
      this.price.hidden = !!this.variantPending;
      if (!this.variantPending) {
        const price = this.info.querySelector('.product-price .product-info__price .price');
        if (price && this.priceSource !== price.outerHTML) {
          const clone = price.cloneNode(true);
          [clone, ...clone.querySelectorAll('*')].forEach((el) => {
            ['id', 'data-dynamic-variant-content', 'data-dynamic-product-content'].forEach((attr) => el.removeAttribute(attr));
          });
          this.price.replaceChildren(clone);
          this.priceSource = price.outerHTML;
        }
      }
      const busy = s.nativeBusy || this.pending;
      this.addReady = !busy && !updating && s.ready;
      this.action.setAttribute('aria-busy', busy ? 'true' : 'false');
      if (busy) {
        this.action.disabled = true;
        this.setText(this.action, 'Adding…');
      } else if (updating) {
        this.action.disabled = true;
        this.setText(this.action, 'Updating…');
      } else if (s.choices.complete && s.matched && !this.available) {
        this.action.disabled = true;
        this.setText(this.action, s.native.textContent.trim());
      } else {
        this.action.disabled = false;
        this.setText(this.action, s.ready ? (s.native.dataset.addToCartText || s.native.textContent.trim()) : 'Choose options');
      }
      this.updateHelp();
    }

    syncSelection(s, updating) {
      if (!this.selectionLabel) {
        this.selectionLabel = document.createElement('span');
        this.quantityEdit = document.createElement('button');
        this.quantityEdit.type = 'button';
        this.quantityEdit.setAttribute('data-ca-sticky-quantity', '');
        this.selection.replaceChildren(this.selectionLabel, this.quantityEdit);
      }
      const q = s.quantityInput;
      const canEdit = !!q && !q.disabled && !q.readOnly && q.type !== 'hidden' && q.getClientRects().length > 0;
      const prefix = s.choices.values.filter(Boolean).join(' / ');
      const quantity = `Qty ${s.quantity || '—'}`;
      this.quantityEdit.hidden = updating || !canEdit;
      this.quantityEdit.disabled = s.nativeBusy || this.pending || updating;
      this.setText(this.selectionLabel, updating ? 'Updating purchase options…'
        : `${prefix}${prefix ? ' · ' : ''}${canEdit ? '' : quantity}`);
      this.setText(this.quantityEdit, quantity);
      this.quantityEdit.setAttribute('aria-label', `Edit quantity, currently ${s.quantity || 'blank'}`);
      if (q?.id) this.quantityEdit.setAttribute('aria-controls', q.id);
      else this.quantityEdit.removeAttribute('aria-controls');
    }

    focusQuantity() {
      const q = this.state().quantityInput;
      if (!q || q.disabled || q.readOnly || q.type === 'hidden' || !q.getClientRects().length) return;
      this.focusInput(q);
    }

    focusInput(target) {
      if (!target) return;
      this.outcome = null;
      this.say('');
      target.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
      target.focus({ preventScroll: true });
    }

    updatePosition() {
      const form = this.mainComponent();
      if (!form) return;
      const rect = (window.innerWidth < 769 ? form : this.info).getBoundingClientRect();
      const out = window.innerWidth < 769 ? rect.bottom > 0 : rect.bottom > 0 && rect.top < window.innerHeight;
      // Unlike intersection transitions alone, this also handles a jump past the form.
      this.panel.classList.toggle('sticky-atc-panel--out', out);
      const close = document.querySelector('clever-search-cb')?.shadowRoot?.querySelector('button[aria-label="Close chatbot"]');
      const chatOpen = !!close && close.getBoundingClientRect().width > 0;
      document.getElementById('site-header')?.classList.toggle('hide', !out && !chatOpen && !document.body.classList.contains('scrolled-to-bottom'));
    }

    activate() {
      const s = this.state();
      if (this.pending || s.nativeBusy) return;
      if (!this.addReady || !s.ready) { this.focusOptions(); return; }
      this.pending = true;
      this.outcome = null;
      this.action.disabled = true;
      this.say('Adding to cart…');
      // Native validation, loading, errors, cart mutation and drawer remain in product-form.js.
      // Preserve native/app click validation as well as the original submit handler.
      s.native.click();
      if (s.native.getAttribute('aria-disabled') !== 'true' && !s.native.classList.contains('is-loading')) {
        this.pending = false;
        this.focusOptions();
      }
      this.queue();
    }

    focusOptions() {
      const s = this.state();
      const target = s.invalid || this.info.querySelector('.product-options input[type="radio"]:checked:not(:disabled)')
        || this.info.querySelector('.product-options input:not([type="hidden"]):not(:disabled), .product-options button, .product-options [tabindex="0"]')
        || s.form?.querySelector('[name="quantity"]') || s.native;
      this.focusInput(target);
      if (s.invalid) {
        this.outcome = 'error';
        this.say(s.invalid.validationMessage || 'Check the highlighted purchase input.');
      }
    }

    attachHelp() {
      const togglerRoot = document.querySelector('clever-search-toggler')?.shadowRoot;
      if (togglerRoot && this.togglerRoot !== togglerRoot) {
        this.togglerObserver?.disconnect();
        this.togglerRoot = togglerRoot;
        this.togglerObserver = new MutationObserver(() => this.updateHelp());
        this.togglerObserver.observe(togglerRoot, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'hidden', 'aria-label'] });
      }
      const chatRoot = document.querySelector('clever-search-cb')?.shadowRoot;
      if (chatRoot && this.chatRoot !== chatRoot) {
        this.chatObserver?.disconnect();
        this.chatRoot = chatRoot;
        this.chatObserver = new MutationObserver(() => this.updateHelp());
        this.chatObserver.observe(chatRoot, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
      }
      this.updateHelp();
    }

    updateHelp() {
      if (!this.panel || !this.isConnected) return;
      const close = document.querySelector('clever-search-cb')?.shadowRoot?.querySelector('button[aria-label="Close chatbot"]');
      const chatOpen = !!close && close.getBoundingClientRect().width > 0;
      this.panel.toggleAttribute('data-ca-help-open', chatOpen);
      const shown = !chatOpen && !this.panel.classList.contains('sticky-atc-panel--out')
        && !document.body.classList.contains('scrolled-to-bottom');
      if (!shown || window.innerWidth >= 769) { this.restoreHelp(); return; }
      const help = document.querySelector('clever-search-toggler')?.shadowRoot?.querySelector('.toggler-wrapper.cs-flt-rb, .toggler-wrapper.cs-flt-lb');
      if (!help || getComputedStyle(help).position !== 'fixed') return;
      if (this.help !== help) {
        this.restoreHelp();
        this.help = help;
        this.helpOriginal = { value: help.style.getPropertyValue('bottom'), priority: help.style.getPropertyPriority('bottom'), bottom: parseFloat(getComputedStyle(help).bottom) || 0 };
        this.helpObserver = new MutationObserver(() => this.updateHelp());
        this.helpObserver.observe(help, { attributes: true, attributeFilter: ['style'] });
      }
      const panelStyle = getComputedStyle(this.panel);
      const panelTopClearance = this.panel.offsetHeight + (parseFloat(panelStyle.bottom) || 0) + (parseFloat(panelStyle.marginBottom) || 0) + 12;
      // Transformed bounding boxes move during reveal; layout height is stable throughout.
      const value = `${Math.ceil(Math.max(this.helpOriginal.bottom, panelTopClearance))}px`;
      if (help.style.getPropertyValue('bottom') !== value || help.style.getPropertyPriority('bottom') !== 'important') help.style.setProperty('bottom', value, 'important');
      this.helpApplied = value;
    }

    restoreHelp() {
      this.helpObserver?.disconnect();
      if (this.help && this.help.style.getPropertyValue('bottom') === this.helpApplied) {
        if (this.helpOriginal.value) this.help.style.setProperty('bottom', this.helpOriginal.value, this.helpOriginal.priority);
        else this.help.style.removeProperty('bottom');
      }
      this.help = null;
      this.helpApplied = null;
    }
  }
  customElements.define('ca-sticky-purchase', CaStickyPurchase);
}
